import { h, clear } from '../utils/dom.js';

const HIGHLIGHT_COLORS = ['yellow', 'green', 'blue', 'purple', 'red'];

/**
 * Build the reference label, combined quote text, and a shareable deep
 * link for the current verse selection (a range collapses to one verse
 * when start === end).
 */
function buildShareData(state, book, chapter, rangeStart, rangeEnd, verses) {
	const bookName = state.chapterData.bookName;
	const ref = rangeStart === rangeEnd ? `${bookName} ${chapter}:${rangeStart}` : `${bookName} ${chapter}:${rangeStart}-${rangeEnd}`;
	const quote = verses.map((v) => v.text).join(' ');

	const url = new URL(window.location.href);
	url.searchParams.set('blb_book', book);
	url.searchParams.set('blb_chapter', String(chapter));
	url.searchParams.set('blb_verse', String(rangeStart));
	url.searchParams.set('blb_translation', state.translation);

	return { ref, quote, url: url.toString() };
}

function renderShareMenu(ref, quote, url) {
	const shareText = `"${quote}" — ${ref}`;

	const links = [
		{ label: 'WhatsApp', icon: '💬', href: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + url)}` },
		{ label: 'X', icon: '𝕏', href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}` },
		{ label: 'Facebook', icon: '📘', href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
		{ label: 'Telegram', icon: '✈️', href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}` },
		{ label: 'Email', icon: '✉️', href: `mailto:?subject=${encodeURIComponent(ref)}&body=${encodeURIComponent(shareText + '\n\n' + url)}` },
	];

	const items = links.map((l) =>
		h(
			'a',
			{
				class: 'blb-share-menu-item',
				href: l.href,
				target: '_blank',
				rel: 'noopener noreferrer',
				onClick: (e) => e.stopPropagation(),
			},
			`${l.icon} ${l.label}`
		)
	);

	items.push(
		h(
			'button',
			{
				class: 'blb-share-menu-item',
				onClick: (e) => {
					e.stopPropagation();
					const btn = e.currentTarget;
					const original = btn.textContent;
					navigator.clipboard
						.writeText(`${shareText}\n${url}`)
						.then(() => {
							btn.textContent = '✅ Copied!';
							window.setTimeout(() => {
								btn.textContent = original;
							}, 1500);
						})
						.catch(() => {});
				},
			},
			'🔗 Copy Verse & Link'
		)
	);

	return h('div', { class: 'blb-share-menu' }, items);
}

function renderVersePopover(state, actions, book, chapter, rangeStart, rangeEnd, verses) {
	const single = rangeStart === rangeEnd;
	const currentHighlight = single ? actions.getHighlight(rangeStart) : null;
	const hasNote = single && state.features.notes && !!actions.getNote(rangeStart);
	const { ref, quote, url } = buildShareData(state, book, chapter, rangeStart, rangeEnd, verses);

	const buttons = [];

	if (state.features.highlights) {
		HIGHLIGHT_COLORS.forEach((color) => {
			buttons.push(
				h('button', {
					class: 'blb-highlight-swatch blb-hl-' + color,
					title: 'Highlight ' + color,
					onClick: (e) => {
						e.stopPropagation();
						actions.setHighlightRange(single && currentHighlight === color ? null : color);
					},
				})
			);
		});
	}

	if (single && state.features.bookmarks) {
		const bookmarked = actions.isBookmarked(rangeStart);
		buttons.push(
			h(
				'button',
				{
					title: bookmarked ? 'Remove bookmark' : 'Add bookmark',
					onClick: (e) => {
						e.stopPropagation();
						actions.toggleBookmark(rangeStart, quote);
					},
				},
				bookmarked ? '⭐' : '☆'
			)
		);
	}

	if (single && state.features.notes) {
		buttons.push(
			h(
				'button',
				{
					title: hasNote ? 'Edit note' : 'Add note',
					onClick: (e) => {
						e.stopPropagation();
						actions.toggleNoteEditor(rangeStart);
					},
				},
				hasNote ? '📝' : '🖊️'
			)
		);
	}

	if (state.features.readAloud && actions.ttsSupported()) {
		buttons.push(
			h(
				'button',
				{
					title: single ? 'Read this verse aloud' : 'Read these verses aloud',
					onClick: (e) => {
						e.stopPropagation();
						actions.speakRange();
					},
				},
				'🔊'
			)
		);
	}

	buttons.push(
		h(
			'button',
			{
				title: 'Share',
				onClick: (e) => {
					e.stopPropagation();
					if (navigator.share) {
						navigator.share({ title: ref, text: `"${quote}" — ${ref}`, url }).catch(() => {});
					} else {
						actions.toggleShareMenu(rangeEnd);
					}
				},
			},
			'📤'
		)
	);

	buttons.push(
		h(
			'button',
			{
				class: 'blb-verse-popover-close',
				title: 'Close',
				onClick: (e) => {
					e.stopPropagation();
					actions.clearSelection();
				},
			},
			'✕'
		)
	);

	const children = [h('div', { class: 'blb-verse-popover-row' }, buttons)];
	if (state.shareMenuVerse === rangeEnd) {
		children.push(renderShareMenu(ref, quote, url));
	}

	return h('div', { class: 'blb-verse-popover' }, children);
}

function renderNoteEditor(state, actions, verse) {
	const existing = actions.getNote(verse);
	let textarea;

	const box = h('div', { class: 'blb-note-editor' }, [
		(textarea = h('textarea', {
			class: 'blb-note-textarea',
			placeholder: 'Write a personal note for this verse…',
		})),
		h('div', { class: 'blb-note-editor-actions' }, [
			h(
				'button',
				{
					class: 'blb-toolbar-btn blb-active',
					onClick: () => {
						actions.saveNote(verse, textarea.value);
					},
				},
				'Save'
			),
			existing
				? h(
						'button',
						{ class: 'blb-toolbar-btn', onClick: () => actions.deleteNote(verse) },
						'Delete'
				  )
				: null,
			h('button', { class: 'blb-toolbar-btn', onClick: () => actions.closeNoteEditor() }, 'Cancel'),
		]),
	]);

	textarea.value = existing ? existing.text : '';

	return box;
}

export function renderReader(container, state, actions) {
	clear(container);

	if (state.loading) {
		container.appendChild(
			h('div', { class: 'blb-empty-state' }, [h('span', { class: 'blb-spinner' }), h('p', null, state.i18n.loading)])
		);
		return;
	}

	if (!state.chapterData) {
		container.appendChild(
			h('div', { class: 'blb-missing-chapter' }, [
				h('p', null, '📖'),
				h('p', null, state.i18n.chapterMissing),
			])
		);
		return;
	}

	const { book, chapter } = state;
	const data = state.chapterData;
	const bookMeta = state.index.books.find((b) => b.id === book);
	const translationMeta = (state.translations || []).find((t) => t.id === state.translation);

	const wrap = h('div', { class: 'blb-reader-inner' });

	wrap.appendChild(
		h('div', { class: 'blb-reader-header' }, [
			h('h2', { class: 'blb-reader-title' }, `${data.bookName} ${data.chapter}`),
			h(
				'p',
				{ class: 'blb-reader-sub' },
				`${bookMeta ? bookMeta.testament === 'OT' ? state.i18n.oldTestament : state.i18n.newTestament : ''} · ${translationMeta ? translationMeta.abbrev : (state.translation || '').toUpperCase()}`
			),
		])
	);

	const versesEl = h('div', { class: 'blb-verses' });

	const hasRange = state.selectedVerse !== null && state.selectionEnd !== null;
	const rangeStart = hasRange ? Math.min(state.selectedVerse, state.selectionEnd) : null;
	const rangeEnd = hasRange ? Math.max(state.selectedVerse, state.selectionEnd) : null;
	const rangeVerses = hasRange ? data.verses.filter((v) => v.verse >= rangeStart && v.verse <= rangeEnd) : [];

	data.verses.forEach((v) => {
		const highlight = actions.getHighlight(v.verse);
		const bookmarked = actions.isBookmarked(v.verse);
		const hasNote = state.features.notes && !!actions.getNote(v.verse);
		const inRange = hasRange && v.verse >= rangeStart && v.verse <= rangeEnd;
		const isSpeaking = state.speaking.active && state.speaking.verse === v.verse;

		const verseEl = h(
			'span',
			{
				class:
					'blb-verse' +
					(inRange ? ' blb-verse-selected' : '') +
					(bookmarked ? ' blb-verse-bookmarked' : '') +
					(hasNote ? ' blb-verse-noted' : '') +
					(isSpeaking ? ' blb-verse-speaking' : ''),
				'data-highlight': highlight || undefined,
				onClick: (e) => {
					e.stopPropagation();
					actions.selectVerse(v.verse);
				},
			},
			[h('span', { class: 'blb-verse-num' }, String(v.verse)), document.createTextNode(v.text + ' ')]
		);

		versesEl.appendChild(verseEl);

		if (hasRange && v.verse === rangeEnd) {
			versesEl.appendChild(renderVersePopover(state, actions, book, chapter, rangeStart, rangeEnd, rangeVerses));
			if (rangeStart === rangeEnd && state.noteEditingVerse === rangeStart) {
				versesEl.appendChild(renderNoteEditor(state, actions, rangeStart));
			}
		}
	});

	wrap.appendChild(versesEl);

	const nav = h('div', { class: 'blb-verse-nav' }, [
		h(
			'button',
			{ class: 'blb-toolbar-btn', onClick: () => actions.prevChapter(), disabled: chapter <= 1 || undefined },
			'← Previous'
		),
		h(
			'button',
			{
				class: 'blb-toolbar-btn',
				onClick: () => actions.nextChapter(),
				disabled: bookMeta && chapter >= bookMeta.chapters ? true : undefined,
			},
			'Next →'
		),
	]);

	wrap.appendChild(nav);
	container.appendChild(wrap);
}
